import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, DropFolderFile } from '../../utils/api';
import Pagination from '../Pagination';

// ── Pick a file from the Drop Folder (Calem, report 296f15b9) ───────────────────────────────────────
// "When a user is requested to upload a document for the completion of a job, direct them to upload
// from the drop folder not their local C drive/PC" — and, asked: "Only from the Drop Folder." The
// Complete popup's documents (JobStatusConfirm) and Closed Jobs' manual-review upload
// (RetroUploadPopup) open this in place of the computer's own file window: the files waiting in the
// shared Drop Folder, newest first, each with who put it there and when. Picking one hands it back to
// the dialog, which files it under the name it shows; the server then moves the Drop Folder copy to
// the Trash (server/src/services/dropFile.ts). data-drop-picker: typing into its search box is work in
// the Complete popup, not typing elsewhere, so JobStatusConfirm's typing pause leaves it alone.

// When it was put there, UK day-first in Bangkok time: "28/09/2026, 14:30".
const fmtAt = (sec: number) => (sec ? new Date(sec * 1000).toLocaleString('en-GB', {
  timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
}) : '—');
// As the File Library's Size column writes it.
const fmtSize = (n: number) => (!n ? '—' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);
const iconOf = (mime: string) => (mime === 'application/pdf' ? 'ti-file-type-pdf' : /^image\//.test(mime) ? 'ti-photo' : 'ti-file');

export default function DropFolderPicker({ docName, onPick, onClose }: {
  docName: string; onPick: (f: DropFolderFile) => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [beat, setBeat] = useState(0);   // Refresh
  const [data, setData] = useState<{ rows: DropFolderFile[]; total: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    // A search waits for a pause in typing; opening, paging and Refresh ask at once.
    const id = setTimeout(() => {
      setLoading(true);
      api.dropFolderFiles(q.trim(), page)
        .then(d => { if (live) { setData({ rows: d.rows || [], total: Number(d.total) || 0, limit: Number(d.limit) || 100 }); setErr(''); setLoading(false); } })
        .catch((e: any) => { if (live) { setErr(e?.message || 'Failed to load the Drop Folder'); setLoading(false); } });
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(id); };
  }, [q, page, beat]);

  const rows = data?.rows || [];
  return (
    <div data-drop-picker onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 580, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <i className="ti ti-folder-open" style={{ color: 'var(--accent)', fontSize: 17 }} aria-hidden />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{t('jdp_title')}</div>
            {docName && <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('jdp_for', { doc: docName })}</div>}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 18, cursor: 'pointer' }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ padding: '10px 20px 9px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input autoFocus value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder={t('jdp_search')}
              style={{ flex: 1, minWidth: 0, padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
            <button type="button" onClick={() => setBeat(b => b + 1)} disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 11.5, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
              <i className="ti ti-refresh" style={{ fontSize: 13 }} aria-hidden />{t('jdp_refresh')}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.45 }}>{t('jdp_hint')}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {err ? <div style={{ fontSize: 12.5, color: '#B42318', padding: '12px 20px' }}>{err}</div>
            : !data ? <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '12px 20px' }}>{t('jdp_loading')}</div>
            : !rows.length ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '14px 20px', lineHeight: 1.5 }}>
                {q.trim() ? t('jdp_no_match', { q: q.trim() }) : t('jdp_empty')}
              </div>
            ) : rows.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <button type="button" onClick={() => onPick(f)}
                  style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '8px 8px 8px 20px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text-primary)' }}>
                  <i className={`ti ${iconOf(f.mime)}`} style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{t('jdp_by', { name: f.byName || '—' })} · {fmtAt(f.at)} · {fmtSize(f.size)}</span>
                  </span>
                </button>
                <button type="button" title={t('jdp_view')} onClick={() => { api.openLibraryFile(f.id, f.name).catch(() => { /* its tab closes again; the list stays */ }); }}
                  style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 16, cursor: 'pointer', padding: '6px 18px 6px 8px' }}>
                  <i className="ti ti-eye" aria-hidden />
                </button>
              </div>
            ))}
        </div>
        {data && <Pagination page={page} total={data.total} limit={data.limit} onPage={setPage} />}
      </div>
    </div>
  );
}
